import type { CambioPerfilPartner, ComisionDelPartner, DireccionFiscal, LiquidacionDelPartner, NegocioDelPartner, Partner, SolicitudPartner } from '../api/ops/partners';
import type { TarjetaAyuda } from '../marco/cabecera';
import type { NombreIcono } from '../componentes/iconos';
import { dinero } from '../logica/dinero';
import { PAISES_ALTA } from '../logica/altaNegocio';

/**
 * Lógica del portal de partners y de su solicitud (1.23, `diseno/plataforma/1.23-portal-y-solicitud-de-partners.html`),
 * sin React. Textos tal cual del prototipo. El panel **no suma ni calcula dinero** (G121, G290; `logica/dinero.ts`): cada
 * importe que se pinta es uno que da la API (la comisión, la base de la cuota, el total de la liquidación). Las sumas del
 * prototipo (lo cobrado a tus negocios en el mes y su variación, la comisión y lo cobrado del año, lo que pagan tus negocios
 * al mes) las da `getPartnerSummary` (G1094).
 */

export type Seccion = 'inicio' | 'negocios' | 'comisiones' | 'enlace' | 'datos' | 'cuenta';
export type VistaComisiones = 'comisiones' | 'liquidaciones';

/** Menú del portal (prototipo `NAV`): clave, etiqueta, etiqueta corta del móvil e icono (clave de `marco/iconosMenu.ts`). */
export const SECCIONES: readonly { clave: Exclude<Seccion, 'cuenta'>; etiqueta: string; corta: string; icono: string }[] = [
    { clave: 'inicio', etiqueta: 'Inicio', corta: 'Inicio', icono: 'home' },
    { clave: 'negocios', etiqueta: 'Negocios', corta: 'Negocios', icono: 'building_2' },
    { clave: 'comisiones', etiqueta: 'Comisiones', corta: 'Comisión', icono: 'percent' },
    { clave: 'enlace', etiqueta: 'Tu enlace', corta: 'Enlace', icono: 'link' },
    { clave: 'datos', etiqueta: 'Datos fiscales', corta: 'Datos', icono: 'file_text' },
];

/** `?seccion=` del portal; `liquidaciones` es Comisiones › Liquidaciones, como en el prototipo. */
export function seccionDe(busqueda: URLSearchParams): { seccion: Seccion; vista: VistaComisiones } {
    const s = busqueda.get('seccion');
    if (s === 'liquidaciones') {
        return { seccion: 'comisiones', vista: 'liquidaciones' };
    }
    const seccion: Seccion = s === 'cuenta' || SECCIONES.some((x) => x.clave === s) ? (s as Seccion) : 'inicio';
    return { seccion, vista: 'comisiones' };
}

export const SUBTITULOS: Record<Exclude<Seccion, 'inicio' | 'cuenta'> | 'liquidaciones', string> = {
    negocios: 'Los negocios que se dieron de alta con tu enlace o tu código.',
    comisiones: 'El {pct} de cada cuota que pagan tus negocios, sin IVA.',
    liquidaciones: 'Cada mes te pagamos lo que sumaron tus comisiones del mes anterior.',
    enlace: 'Compártelo. Cada negocio que se dé de alta con él queda atribuido a ti.',
    datos: 'Con estos datos hacemos tus liquidaciones y sus facturas.',
};

const t = (icono: NombreIcono, titulo: string, texto: string): TarjetaAyuda => ({ icono, titulo, texto });

/** Tarjetas de la «?» de cada sección (prototipo `HELP`). */
export function ayudaDe(clave: Exclude<Seccion, 'cuenta'> | 'liquidaciones', pct: string): readonly TarjetaAyuda[] {
    switch (clave) {
        case 'inicio':
            return [t('house', 'Tu inicio', 'Lo que te llevas este mes, de qué negocio sale y lo que pide tu atención, abajo.'), t('wallet', 'Cuándo cobras', 'El día 5 cerramos el mes anterior y te lo pagamos por transferencia antes del 15.')];
        case 'negocios':
            return [t('building-2', 'Negocios atribuidos', 'Los que se dieron de alta con tu enlace o tu código. No entras en su panel.'), t('percent', `Tu ${pct}`, 'Se calcula sobre la cuota sin IVA. Los negocios en prueba o con impago no suman ese mes.')];
        case 'comisiones':
            return [t('percent', 'Comisiones', 'Una línea por negocio con la cuota del mes que elijas arriba.'), t('rotate-ccw', 'Reembolsos e impagos', 'Si un negocio no paga o se le devuelve la cuota, ese mes no suma.')];
        case 'liquidaciones':
            return [t('wallet', 'Liquidaciones', 'El día 5 cerramos las comisiones del mes anterior y te las pagamos por transferencia antes del 15.'), t('download', 'Factura', 'Cada liquidación pagada tiene su factura para descargar.')];
        case 'enlace':
            return [t('link', 'Atribución', 'El negocio queda atribuido si se da de alta con tu enlace o escribe tu código. Si se dio de alta sin él, Reservaria puede atribuírtelo a mano.'), t('lock', 'Tu código no cambia', 'No se regenera: así siguen valiendo los enlaces que ya has repartido.')];
        case 'datos':
            return [t('file-text', 'Datos fiscales', 'Con ellos hacemos las facturas de tus liquidaciones. Los cambios valen desde la próxima.'), t('lock', 'IBAN', 'Lo guardamos cifrado y solo enseñamos los últimos 4 números.')];
    }
}

/** «Cómo funciona la atribución» (Tu enlace). */
export function reglasAtribucion(pct: string): readonly [NombreIcono, string][] {
    return [
        ['users', 'Un negocio solo tiene un partner: el de su alta. Si hay que cambiarlo, lo hace Reservaria con motivo.'],
        ['percent', `Cobras el ${pct} de la cuota sin IVA que paga cada negocio, mientras siga con Reservaria.`],
        ['lock', 'Tu código y tu enlace no cambian nunca: así siguen valiendo los que ya has repartido.'],
        ['eye-off', 'No entras en el panel de tus negocios ni ves a sus clientes.'],
    ];
}

/** Condiciones del programa (paso 3 de la solicitud). */
export function condicionesPrograma(pct: string): readonly [NombreIcono, string][] {
    return [
        ['users', 'Un negocio solo tiene un partner: el de su alta. Si hay que cambiarlo, lo hace Reservaria con motivo.'],
        ['percent', `Cobras el ${pct} de las cuotas sin IVA que paga cada negocio, mientras siga con Reservaria.`],
        ['wallet', 'La liquidación de cada mes se cierra el día 5 y te la pagamos por transferencia antes del 15.'],
        ['lock', 'Tu código no cambia nunca. No entras en el panel de tus negocios.'],
    ];
}

/** Porcentaje del programa antes de tener el perfil (la solicitud): el del prototipo. */
export const PCT_PROGRAMA = '20 %';

/** «20 %» desde `commission_rate_bp` (2000 = 20 %): solo cambia la unidad, no calcula dinero. */
export function porcentaje(bp: number): string {
    return `${(bp / 100).toLocaleString('es-ES', { maximumFractionDigits: 2 })} %`;
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const mayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Mes de una liquidación o de una comisión: año y mes (0-11). */
export interface Mes {
    y: number;
    m: number;
}

export const claveMes = (x: Mes): string => `${String(x.y)}-${String(x.m + 1).padStart(2, '0')}`;
export const nombreMes = (x: Mes): string => MESES[x.m] ?? '';
export const mesCorto = (x: Mes): string => MESES_CORTOS[x.m] ?? '';
/** «Octubre de 2026». */
export const textoMes = (x: Mes): string => `${mayuscula(nombreMes(x))} de ${String(x.y)}`;
export const mesSiguiente = (x: Mes): Mes => (x.m === 11 ? { y: x.y + 1, m: 0 } : { y: x.y, m: x.m + 1 });

/** Zona de los meses de las liquidaciones (G1087: la hora de Madrid). */
const ZONA = 'Europe/Madrid';

/** Mes (en Madrid) de un instante ISO. */
export function mesDe(iso: string, zona = ZONA): Mes {
    const partes = new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit' }).formatToParts(new Date(iso));
    const v = (tipo: string) => Number(partes.find((p) => p.type === tipo)?.value ?? '0');
    return { y: v('year'), m: v('month') - 1 };
}

/** Día del mes (en Madrid) de un instante ISO. */
export function diaDe(iso: string, zona = ZONA): number {
    return Number(new Intl.DateTimeFormat('en-CA', { timeZone: zona, day: 'numeric' }).format(new Date(iso)));
}

/** Mes de una liquidación (`period`, primer día del mes). */
export function mesDePeriodo(period: string): Mes {
    const [y = '0', m = '1'] = period.split('-');
    return { y: Number(y), m: Number(m) - 1 };
}

/** «el 3 de marzo de 2025». */
export function fechaLarga(iso: string): string {
    const m = mesDe(iso);
    return `${String(diaDe(iso))} de ${nombreMes(m)} de ${String(m.y)}`;
}

/** Las comisiones de un mes (en Madrid), en el orden de la API. */
export function comisionesDelMes(comisiones: readonly ComisionDelPartner[], mes: Mes): ComisionDelPartner[] {
    return comisiones.filter((c) => claveMes(mesDe(c.created_at)) === claveMes(mes));
}

/** La comisión de un negocio en un mes (la que no se ha anulado), si la hay, por su `business_id` (G1104). */
export function comisionDelNegocio(comisiones: readonly ComisionDelPartner[], negocio: string, mes: Mes): ComisionDelPartner | undefined {
    return comisionesDelMes(comisiones, mes).find((c) => c.business_id === negocio && c.status !== 'reversed');
}

/** La liquidación de un mes, si la hay. */
export function liquidacionDelMes(liquidaciones: readonly LiquidacionDelPartner[], mes: Mes): LiquidacionDelPartner | undefined {
    return liquidaciones.find((l) => claveMes(mesDePeriodo(l.period)) === claveMes(mes));
}

/** La liquidación en curso (la del mes abierto), si la hay. */
export function liquidacionEnCurso(liquidaciones: readonly LiquidacionDelPartner[]): LiquidacionDelPartner | undefined {
    return liquidaciones.find((l) => l.status === 'open');
}

/** Línea de una liquidación (Comisiones › Liquidaciones). */
export function lineaLiquidacion(l: LiquidacionDelPartner): string {
    const siguiente = nombreMes(mesSiguiente(mesDePeriodo(l.period)));
    switch (l.status) {
        case 'open':
            return `En curso · se cierra el 5 de ${siguiente} y se paga antes del 15`;
        case 'prepared':
            return `Cerrada · te la pagamos antes del 15 de ${siguiente}`;
        case 'paid':
            return l.paid_at == null ? 'Pagada · por transferencia' : `Pagada el ${String(diaDe(l.paid_at))} de ${nombreMes(mesDe(l.paid_at))} · por transferencia`;
    }
}

/** Estado de la comisión de un negocio en la lista del mes. */
export function estadoComision(c: ComisionDelPartner): { texto: string; noSuma: boolean } {
    switch (c.status) {
        case 'pending':
            return { texto: 'Por liquidar', noSuma: false };
        case 'settled':
            return { texto: 'Liquidada', noSuma: false };
        case 'reversed':
            return { texto: 'No suma', noSuma: true };
    }
}

/** Línea de la comisión de un negocio: «Cuota de octubre cobrada el 1 · 146,25 €». */
export function lineaComision(c: ComisionDelPartner): string {
    const mes = nombreMes(mesDe(c.created_at));
    return c.status === 'reversed' ? `Cuota de ${mes} devuelta · ${dinero(c.base)}` : `Cuota de ${mes} cobrada el ${String(diaDe(c.created_at))} · ${dinero(c.base)}`;
}

/** Línea de un negocio atribuido: «Sevilla · Base y 6 bloques · 146,25 € al mes · desde marzo de 2025» (lo que llegue). */
export function lineaNegocio(n: NegocioDelPartner, movil: boolean): string {
    const desde = n.attributed_at === undefined ? null : mesDe(n.attributed_at);
    const partes = movil
        ? [n.city ?? null, `${dinero(n.monthly_fee)}/mes`]
        : [n.city ?? null, n.plan_summary ?? null, `${dinero(n.monthly_fee)} al mes`, desde === null ? null : `desde ${nombreMes(desde)} de ${String(desde.y)}`];
    return partes.filter((p): p is string => p !== null && p !== '').join(' · ');
}

/** «Impago» (el único chip de la lista: explica por qué no suma; «Activo» es lo normal y no lleva). */
export const conImpago = (n: NegocioDelPartner): boolean => n.subscription_status === 'past_due';

/**
 * «Partner desde marzo de 2025» (tarjeta de tu cuenta, prototipo): de `approved_at` (G1096); sin aprobar, nada. Sustituye al
 * rol de la persona, que el diseño quita (C.57).
 */
export function partnerDesde(approvedAt: string | null | undefined): string {
    if (approvedAt == null) {
        return '';
    }
    const m = mesDe(approvedAt);
    return `Partner desde ${nombreMes(m)} de ${String(m.y)}`;
}

/** Variación frente al mes anterior en puntos básicos (1000 = +10 %): «+10 %», «−5,5 %»; nula si no hay mes anterior. Solo cambia la unidad. */
export function variacion(bp: number | null | undefined): { texto: string; sentido: 'sube' | 'baja' | 'igual' } | null {
    if (bp == null) {
        return null;
    }
    const pct = (Math.abs(bp) / 100).toLocaleString('es-ES', { maximumFractionDigits: 1 });
    return bp === 0 ? { texto: '0 %', sentido: 'igual' } : { texto: `${bp > 0 ? '+' : '−'}${pct} %`, sentido: bp > 0 ? 'sube' : 'baja' };
}

/** «Versión de marzo de 2025 · aceptada el 3 de marzo de 2025» (lo que llegue; nulos en las solicitudes antiguas). */
export function textoCondiciones(p: Pick<Partner, 'terms_version' | 'terms_accepted_at'>): string | null {
    const version = p.terms_version ?? null;
    const cuando = p.terms_accepted_at ?? null;
    if (version === null && cuando === null) {
        return null;
    }
    const v = version === null ? null : /^\d{4}-\d{2}$/.test(version) ? `Versión de ${nombreMes(mesDePeriodo(version))} de ${version.slice(0, 4)}` : `Versión ${version}`;
    return [v, cuando === null ? null : `aceptada el ${fechaLarga(cuando)}`].filter((x): x is string => x !== null).join(' · ');
}

export const TIPOS_PARTNER: readonly { valor: Partner['type']; texto: string }[] = [
    { valor: 'association', texto: 'Asociación' },
    { valor: 'consultant', texto: 'Consultor' },
    { valor: 'pos_vendor', texto: 'Vendedor de TPV' },
    { valor: 'other', texto: 'Otro' },
];
export const textoTipo = (tipo: Partner['type']): string => TIPOS_PARTNER.find((x) => x.valor === tipo)?.texto ?? '';

/** Etiquetas del nombre fiscal y del NIF según la forma jurídica. */
export function etiquetasFiscales(forma: Partner['legal_form'] | undefined): { nombre: string; nif: string; forma: string } {
    return forma === 'self_employed' ? { nombre: 'Nombre fiscal', nif: 'NIF', forma: 'Autónomo' } : { nombre: 'Razón social', nif: 'CIF', forma: 'Empresa' };
}

/** IBAN guardado: solo los 4 últimos (G188). */
export const ibanOculto = (ultimos: string | null | undefined): string => (ultimos == null || ultimos === '' ? '' : `•••• •••• •••• •••• •••• ${ultimos}`);

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const emailBien = (v: string): boolean => EMAIL.test(v.trim());
/** Teléfono con prefijo (E.164, como el contrato); se aceptan espacios al escribir. */
export const telefonoE164 = (v: string): string => v.replace(/[\s.-]+/g, '');
export const telefonoBien = (v: string): boolean => /^\+[1-9][0-9]{6,14}$/.test(telefonoE164(v));
export const ibanLimpio = (v: string): string => v.replace(/\s/g, '').toUpperCase();
export const ibanBien = (v: string): boolean => /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(ibanLimpio(v));

export const ERRORES = {
    rellena: 'Rellena este campo.',
    email: 'Escribe tu email.',
    emailMal: 'Ese email no parece bien escrito.',
    emailFacturas: 'Escribe el email para las facturas.',
    telefono: 'Escribe un teléfono válido.',
    contrasena: 'Mínimo 12 caracteres.',
    iban: 'Escribe el IBAN.',
    ibanMal: 'Revisa el IBAN: empieza por ES y lleva 24 caracteres.',
    condiciones: 'Acepta las condiciones para enviar la solicitud.',
    robot: 'No hemos podido comprobar que no eres un robot. Vuelve a enviarla: lo que has escrito sigue aquí.',
} as const;

/**
 * Dirección fiscal del formulario en cuatro campos: Dirección, CP, Ciudad y País (G1095; el contrato la pide por partes,
 * `PostalAddress`, y el país ya no sale del IBAN). Pendiente de C.57: el dibujo de los cuatro campos.
 */
export interface Direccion {
    calle: string;
    cp: string;
    ciudad: string;
    pais: string;
}

/** Países del desplegable: los del alta de un negocio (1.5), con el guardado aunque no esté en la lista. */
export function paisesFiscales(actual: string): readonly { valor: string; texto: string }[] {
    const lista = PAISES_ALTA.map(([valor, texto]) => ({ valor, texto }));
    return actual === '' || lista.some((p) => p.valor === actual) ? lista : [...lista, { valor: actual, texto: actual }];
}

export const PAIS_POR_DEFECTO = 'ES';

export const direccionDe = (d: DireccionFiscal | null | undefined): Direccion => ({ calle: d?.line1 ?? '', cp: d?.postal_code ?? '', ciudad: d?.city ?? '', pais: d?.country ?? PAIS_POR_DEFECTO });

export function aDireccionFiscal(d: Direccion, anterior?: DireccionFiscal | null): DireccionFiscal {
    return { line1: d.calle.trim(), postal_code: d.cp.trim(), city: d.ciudad.trim(), country: d.pais, line2: anterior?.line2 ?? null, province: anterior?.province ?? null };
}

export function erroresDireccion(d: Direccion): Partial<Record<keyof Direccion, string>> {
    const e: Partial<Record<keyof Direccion, string>> = {};
    if (d.calle.trim() === '') e.calle = ERRORES.rellena;
    if (d.cp.trim() === '') e.cp = ERRORES.rellena;
    if (d.ciudad.trim() === '') e.ciudad = ERRORES.rellena;
    if (d.pais.trim() === '') e.pais = ERRORES.rellena;
    return e;
}

// ---- Solicitud (3 pasos) ----

export interface FormSolicitud {
    nombre: string;
    email: string;
    telefono: string;
    contrasena: string;
    forma: 'self_employed' | 'company';
    tipo: Partner['type'];
    nombreFiscal: string;
    nif: string;
    direccion: Direccion;
    emailFacturas: string;
    iban: string;
    acepta: boolean;
}

export const SOLICITUD_VACIA: FormSolicitud = {
    nombre: '',
    email: '',
    telefono: '',
    contrasena: '',
    forma: 'company',
    tipo: 'consultant',
    nombreFiscal: '',
    nif: '',
    direccion: { calle: '', cp: '', ciudad: '', pais: PAIS_POR_DEFECTO },
    emailFacturas: '',
    iban: '',
    acepta: false,
};

export type ErroresSolicitud = Partial<Record<'nombre' | 'email' | 'telefono' | 'contrasena' | 'nombreFiscal' | 'nif' | 'calle' | 'cp' | 'ciudad' | 'pais' | 'emailFacturas' | 'iban' | 'acepta', string>>;

/** Errores del paso (0, 1 o 2) antes de seguir. */
export function erroresDelPaso(paso: number, f: FormSolicitud): ErroresSolicitud {
    const e: ErroresSolicitud = {};
    if (paso === 0) {
        if (f.nombre.trim() === '') e.nombre = ERRORES.rellena;
        if (f.email.trim() === '') e.email = ERRORES.email;
        else if (!emailBien(f.email)) e.email = ERRORES.emailMal;
        if (f.telefono.trim() === '') e.telefono = ERRORES.rellena;
        else if (!telefonoBien(f.telefono)) e.telefono = ERRORES.telefono;
        if (f.contrasena.length < 12) e.contrasena = ERRORES.contrasena;
    } else if (paso === 1) {
        if (f.nombreFiscal.trim() === '') e.nombreFiscal = ERRORES.rellena;
        if (f.nif.trim() === '') e.nif = ERRORES.rellena;
        Object.assign(e, erroresDireccion(f.direccion));
        if (f.emailFacturas.trim() === '') e.emailFacturas = ERRORES.emailFacturas;
        else if (!emailBien(f.emailFacturas)) e.emailFacturas = ERRORES.emailMal;
        if (f.iban.trim() === '') e.iban = ERRORES.iban;
        else if (!ibanBien(f.iban)) e.iban = ERRORES.ibanMal;
    } else if (paso === 2 && !f.acepta) {
        e.acepta = ERRORES.condiciones;
    }
    return e;
}

/**
 * Cuerpo de `applyAsPartner`. Sin `terms_version`: la versión vigente de las condiciones la fija y la guarda el back
 * (G1099). El país fiscal (`tax_country`) es el de la dirección fiscal (G1095).
 */
export function datosSolicitud(f: FormSolicitud): SolicitudPartner {
    const pais = f.direccion.pais;
    return {
        email: f.email.trim(),
        password: f.contrasena,
        display_name: f.nombre.trim(),
        type: f.tipo,
        legal_form: f.forma,
        legal_name: f.nombreFiscal.trim(),
        tax_id: f.nif.trim().toUpperCase(),
        tax_country: pais,
        phone: telefonoE164(f.telefono),
        billing_address: aDireccionFiscal(f.direccion),
        billing_email: f.emailFacturas.trim(),
        iban: ibanLimpio(f.iban),
    };
}

// ---- Datos fiscales del portal ----

export interface FormDatos {
    emailFacturas: string;
    telefono: string;
    direccion: Direccion;
    /** IBAN nuevo; null mientras no se pulsa «Cambiar el IBAN». */
    iban: string | null;
}

export const formDatosDe = (p: Partner): FormDatos => ({ emailFacturas: p.billing_email ?? '', telefono: p.phone ?? '', direccion: direccionDe(p.billing_address), iban: null });

export type ErroresDatos = Partial<Record<'emailFacturas' | 'telefono' | 'calle' | 'cp' | 'ciudad' | 'pais' | 'iban', string>>;

export function erroresDatos(f: FormDatos): ErroresDatos {
    const e: ErroresDatos = { ...erroresDireccion(f.direccion) };
    if (!emailBien(f.emailFacturas)) e.emailFacturas = ERRORES.emailMal;
    if (f.telefono.trim() === '') e.telefono = ERRORES.rellena;
    else if (!telefonoBien(f.telefono)) e.telefono = ERRORES.telefono;
    if (f.iban !== null && !ibanBien(f.iban)) e.iban = ERRORES.ibanMal;
    return e;
}

/** Cuerpo de `updatePartnerProfile` con solo lo que ha cambiado (null si nada). */
export function cambiosDatos(p: Partner, f: FormDatos): CambioPerfilPartner | null {
    const c: CambioPerfilPartner = {};
    if (f.emailFacturas.trim() !== (p.billing_email ?? '')) c.billing_email = f.emailFacturas.trim();
    if (telefonoE164(f.telefono) !== (p.phone ?? '')) c.phone = telefonoE164(f.telefono);
    const antes = direccionDe(p.billing_address);
    if (f.direccion.calle.trim() !== antes.calle || f.direccion.cp.trim() !== antes.cp || f.direccion.ciudad.trim() !== antes.ciudad || f.direccion.pais !== antes.pais) {
        // La dirección va entera, con sus cuatro campos (G1095).
        c.billing_address = aDireccionFiscal(f.direccion, p.billing_address);
    }
    if (f.iban !== null && f.iban.trim() !== '') c.iban = ibanLimpio(f.iban);
    return Object.keys(c).length === 0 ? null : c;
}
